/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Grid, Paper, Popper } from '@mui/material';
import type { FC } from 'react';
import { AnimationLengthButtonConnect } from '../TimeSliderConnect/TimeSliderButtonsConnect/AnimationLengthButtonConnect';
import { AutoUpdateButtonConnect } from '../TimeSliderConnect/TimeSliderButtonsConnect/AutoUpdateButtonConnect';
import { BackwardForwardStepButtonConnect } from '../TimeSliderConnect/TimeSliderButtonsConnect/BackwardForwardStepButtonConnect';
import { NowButtonConnect } from '../TimeSliderConnect/TimeSliderButtonsConnect/NowButtonConnect';
import { PlayButtonConnect } from '../TimeSliderConnect/TimeSliderButtonsConnect/PlayButtonConnect';
import { SpeedButtonConnect } from '../TimeSliderConnect/TimeSliderButtonsConnect/SpeedButtonConnect';
import { TimeSpanButtonConnect } from '../TimeSliderConnect/TimeSliderButtonsConnect/TimeSpanButtonConnect';
import { TimeStepButtonConnect } from '../TimeSliderConnect/TimeSliderButtonsConnect/TimeStepButtonConnect';

const POPPER_WIDTH = 440;
const POPPER_LENGTH = 270;

export type PopperPlacement = 'right' | 'left' | 'top' | 'bottom';

export const getPopperplacement = (
  buttonElement: HTMLButtonElement | null,
  mapId: string,
): PopperPlacement => {
  const button = buttonElement?.getBoundingClientRect();
  const mapWindow = document
    .getElementById(`mapcontainer_${mapId}`)
    ?.getBoundingClientRect();
  if (button && mapWindow) {
    const popperIsOutsideWindowOnLeft =
      mapWindow.left > button.left - POPPER_WIDTH;
    const popperIsOutsideWindowOnBottom =
      button.bottom + POPPER_LENGTH > mapWindow.bottom;
    const popperIsOutsideWindowOnRight =
      button.right + POPPER_WIDTH > mapWindow.right;

    if (!popperIsOutsideWindowOnLeft) {
      return 'left';
    }
    if (!popperIsOutsideWindowOnBottom) {
      return 'bottom';
    }
    if (!popperIsOutsideWindowOnRight) {
      return 'right';
    }
    return 'top';
  }
  return 'left';
};

export interface OptionsMenuProps {
  isOpen?: boolean;
  popperPlacement: PopperPlacement;
  buttonElement: HTMLButtonElement | null;
  mapId: string;
}

export const OptionsMenu: FC<OptionsMenuProps> = ({
  isOpen = false,
  buttonElement,
  popperPlacement,
  mapId,
}) => {
  if (!buttonElement) {
    return null;
  }
  const direction = ['bottom', 'top'].includes(popperPlacement)
    ? 'column'
    : 'row';

  return (
    <Popper
      anchorEl={buttonElement}
      open={isOpen}
      placement={popperPlacement}
      sx={{ zIndex: 1002 }}
    >
      <Paper elevation={3}>
        <Grid
          container
          spacing={0.5}
          sx={{
            padding: '8px',
            ...(direction === 'column' && {
              '& button': { width: '100%!important' },
            }),
            flexDirection: direction === 'column' ? 'column' : 'row',
          }}
        >
          <NowButtonConnect mapId={mapId} sourceId={mapId} />
          <TimeSpanButtonConnect mapId={mapId} />
          <AnimationLengthButtonConnect mapId={mapId} />
          <TimeStepButtonConnect mapId={mapId} />
          <SpeedButtonConnect mapId={mapId} />
          <AutoUpdateButtonConnect mapId={mapId} />
          <BackwardForwardStepButtonConnect
            isForwardStep={false}
            mapId={mapId}
          />
          <BackwardForwardStepButtonConnect
            isForwardStep={true}
            mapId={mapId}
          />
          <PlayButtonConnect mapId={mapId} />
        </Grid>
      </Paper>
    </Popper>
  );
};
